import { useUser } from '@src/hooks/useUser';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import { ExecTypeEnum } from '@talos/kyoko';
import { throttle } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, type PropsWithChildren } from 'react';
import { SoundContext } from './SoundContext';
import { AvailableSounds, type OrderTypeToSoundMapType } from './SoundProviderUtils';
import { useSoundSettings } from './SoundSettingsContext';
import { playSound } from './soundUtils';

/** @deprecated - Sounds management is moving to Notifications.
 * - Discuss with the team before using this provider more than it already is
 */
export const SoundProvider = function SoundProvider({ children }: PropsWithChildren) {
  const { executionReportsByClOrdID } = useExecutionReports();
  const { Name: currUser } = useUser();

  const { enableSoundEffects, enabledSounds } = useSoundSettings();

  const playContextSound = useCallback(
    (sound: OrderTypeToSoundMapType, force = false) => {
      // `Force` allows the provider to still play the sound from the settings page even if the sound is disabled
      if (force || (enableSoundEffects && enabledSounds[sound])) {
        playSound(sound);
      }
    },
    [enableSoundEffects, enabledSounds]
  );

  const playSoundThrottled = useMemo(
    () =>
      throttle(
        (sound: OrderTypeToSoundMapType, force = false) => {
          playContextSound(sound, force);
        },
        1000,
        { leading: true, trailing: false }
      ),
    [playContextSound]
  );

  const processedIDs = useRef(new Set<string>());
  useEffect(() => {
    const subscription = executionReportsByClOrdID?.subscribe(data => {
      for (const { ExecType, ClOrdID, User } of data.values()) {
        if (processedIDs.current.has(ClOrdID) || currUser !== User) {
          continue;
        }
        processedIDs.current.add(ClOrdID);
        switch (ExecType) {
          case ExecTypeEnum.DoneForDay:
            playContextSound(AvailableSounds.Filled_Order);
            break;
          case ExecTypeEnum.Rejected:
            playContextSound(AvailableSounds.Rejected_Order);
            break;
          case ExecTypeEnum.Canceled:
            playContextSound(AvailableSounds.Canceled_Order);
            break;
          default:
            processedIDs.current.delete(ClOrdID);
            break;
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [currUser, executionReportsByClOrdID, playContextSound]);

  return (
    <SoundContext.Provider value={{ playSound: playContextSound, playSoundThrottled }}>
      {children}
    </SoundContext.Provider>
  );
};
